import { apiClient } from "@/api/client";
import {
  DeletedItem,
  DeletedItemsResponse,
  RestoreResponse,
  RestoreResultData,
  toRestoreError,
} from "@/api/restore";
import axios, { AxiosError } from "axios";
import { create } from "zustand";

interface CampaignMetadata {
  id: string;
  name: string;
  project_code: string;
  budget: number;
  file_naming_convention: string;
  campaign_poster: string | null;
  status: string;
  progress_status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface Agency {
  relation_id: string;
  media_agency_id: string;
  media_agency_name: string;
}

interface CampaignUser {}

interface Team {
  campaign_team_id: string;
  team_id: string;
  name: string;
  team_type: string;
  all_users: any[];
  campaign_users: CampaignUser[];
}

interface Language {
  campaign_language_id: string;
  language_id: string;
  name: string;
}

interface Parameter {
  parameter_id: string;
  key: string;
  value: string;
  value_json: { key: string; value: string };
}

interface AdRow {
  asset_id: string;
  publisher_id: string;
  publisher_name: string;
  ad_type: string;
  due_date?: string | null;
  versions: number;
  parameters: Parameter[];
  status_counts?: StatusCounts;
}

interface FlightAdCategory {
  flight_ad_categories_id: string;
  ad_category_id: string | null;
  ad_category_name: string | null;
  ad_rows: AdRow[];
}

interface FlightRow {
  flight_row_id: string;
  language: string;
  objective: string;
  region: string;
  flight_ad_categories: FlightAdCategory[];
}

interface Flight {
  flight_id: string;
  flight_number: number;
  start_date: string;
  end_date: string;
  flight_rows: FlightRow[];
  status_counts?: StatusCounts;
}

interface Asset {
  asset_id: string;
  flight_id: string;
  publisher_id: string;
  ad_type: string;
  region: string;
}

interface Metrics {
  total_assets: number;
  unique_publishers: number;
  unique_ad_types: number;
}

export interface StatusCounts {
  briefed: number;
  in_progress: number;
  in_review: number;
  approved: number;
  trafficking: number;
  live: number;
  draft: number;
  total: number;
}

interface Objective {
  objective_id: string;
  objective_name: string;
  assets: Asset[];
  metrics: Metrics;
  status_counts?: StatusCounts;
  header_image: {
    id: string;
    key: string;
    language_id: string;
    mime: string;
    objective_id: string;
    type: string;
    url: string;
  };
}

interface LanguageObjective {
  language_id: string;
  language_name: string;
  objectives: Objective[];
}

/**
 * One Box in the ad-category view.
 *
 * Despite the name this is an *asset row*, not an ad type: a flight row can carry several
 * Boxes of the same ad type, each an independent asset with its own versions, parameters,
 * preview and status counts. `asset_id` is the identity — `ad_type_id` is shared by
 * siblings and must never be used as a key or a lookup.
 */
interface AdType {
  asset_id: string;
  ad_type_id: string;
  ad_type_name: string;
  /** Optional: absent on older cached payloads, so the tile label degrades. */
  publisher_name?: string | null;
  /** The flight row this Box belongs to, and its region — what distinguishes siblings. */
  flight_row_id?: string | null;
  region?: string | null;
  status_counts?: StatusCounts;
  ratio: string | null;
  header_image?: string | null;
  /** This Box's own preview, emitted per asset by the backend. */
  display_payload?: DisplayPayload | null;
}

interface AdCategory {
  ad_category_id: string;
  ad_category_name: string;
  total_ads: number;
  status_counts?: StatusCounts;
  ad_types: AdType[];
}

interface DashboardLanguage {
  language_id: string;
  name: string;
}

interface DashboardObjective {
  objective_id: string;
  name: string;
}

interface DashboardMetadata {
  campaign: CampaignMetadata;
  agency: Agency[];
  teams: Team[];
  users: any[];
  language: DashboardLanguage;
  objective: DashboardObjective;
}

type PreviewStatus = "pending" | "ready" | "failed";

interface DisplayPreviewEntry {
  version_id: string;
  version_number: number;
  status: PreviewStatus;
  url: string | null;
}

interface DisplayPayload {
  source: "manual" | "auto" | "none";
  manual_image: { url: string | null } | null;
  auto_previews: DisplayPreviewEntry[];
}

interface DashboardFlightAdRow {
  asset_id: string;
  publisher_id: string;
  ad_type: string;
  versions: number;
  parameters: Parameter[];
  // Additive per-Box preview data emitted by the backend asset-preview-pipeline.
  // Optional so existing consumers/responses without it stay backwards compatible.
  display_payload?: DisplayPayload;
}

interface DashboardFlightAdCategory {
  flight_ad_categories_id: string;
  ad_category_id: string;
  ad_category_name: string;
  ad_rows: DashboardFlightAdRow[];
}

interface DashboardFlightRow {
  flight_row_id: string;
  language: string;
  objective: string;
  region: string;
  flight_ad_categories: DashboardFlightAdCategory[];
}

interface DashboardFlight {
  flight_id: string;
  flight_number: number;
  start_date: string;
  end_date: string;
  flight_rows: DashboardFlightRow[];
}

interface DashboardStats {
  total_assets: number;
  unique_ad_types: number;
}

interface LanguageObjectiveDashboardData {
  metadata: DashboardMetadata;
  nested_by_ad_category: AdCategory[];
  nested_by_flight: DashboardFlight[];
  stats: DashboardStats;
}

interface LanguageObjectiveDashboardResponse {
  success: boolean;
  message: string;
  data: LanguageObjectiveDashboardData;
}

interface CampaignData {
  metadata: {
    campaign: CampaignMetadata;
    agency: Agency[];
    teams: Team[];
    users: any[];
    languages: Language[];
    header_image: {
      id: string;
      key: string;
      mime: string;
      type: string;
      url: string;
    };
    status_counts?: StatusCounts;
  };
  nested_by_flight: Flight[];
  nested_by_language_objective: LanguageObjective[];
}

interface CampaignDetailResponse {
  success: boolean;
  message: string;
  data: CampaignData;
}

interface UploadDashboardImageRequest {
  campaign_id: string;
  type: string;
  key?: string;
  filename?: string;
  mime: string;
}

interface UploadAssetsDashboardImageRequest {
  campaign_id: string;
  type: string;
  objective_id: string;
  language_id: string;
  key?: string;
  filename?: string;
  mime: string;
}

interface UploadDashboardImageResponse {
  success: boolean;
  message: string;
  data: { id: string; key: string; mime: string; type: string; url: string };
}

interface AssetPreviewOverrideResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    key: string;
    mime: string;
    type: string;
    asset_id: string;
    url: string;
  };
}

interface InitiateUploadResponse {
  success: boolean;
  message: string;
  data: { upload_url: string; key: string; mime: string };
}

interface CampaignDetailState {
  campaignDetail: CampaignData | null;
  languageObjectiveDashboard: LanguageObjectiveDashboardData | null;
  getCampaignDetailData: (campaignId: string) => Promise<void>;
  getLanguageObjectiveDashboard: (
    campaignId: string,
    languageId: string,
    objectiveId: string,
  ) => Promise<void>;
  putS3Image: (presignedUrl: string, file: File) => Promise<void>;
  uploadDashboardImage: (
    campaignId: string,
    requestBody: UploadDashboardImageRequest,
  ) => Promise<UploadDashboardImageResponse | InitiateUploadResponse>;
  uploadAssetsDashboardImage: (
    campaignId: string,
    requestBody: UploadAssetsDashboardImageRequest,
  ) => Promise<UploadDashboardImageResponse | InitiateUploadResponse>;
  deleteFlight: (flightId: string) => Promise<void>;
  deleteAsset: (assetId: string) => Promise<void>;
  // Undo of a delete. Both reject with a RestoreError carrying the server's
  // `error_code` so callers can tell "already restored" (404
  // NO_RESTORABLE_DELETION) apart from a blocking ancestor (409) and a genuine
  // failure.
  restoreFlight: (flightId: string) => Promise<RestoreResultData>;
  restoreAsset: (assetId: string) => Promise<RestoreResultData>;
  // The campaign's restorable deletions, newest first. Blocked rows are
  // included with `restorable: false`.
  deletedItems: DeletedItem[];
  getDeletedItems: (campaignId: string) => Promise<void>;
  // Per-Box manual preview override (asset-preview-pipeline, Requirement 8).
  // Scoped to a single Box's asset row (asset_id), independent of the ad-type
  // header image. Upload replaces any existing override for that Box.
  uploadAssetPreviewOverride: (
    campaignId: string,
    assetId: string,
    file: File,
  ) => Promise<AssetPreviewOverrideResponse>;
  removeAssetPreviewOverride: (
    campaignId: string,
    assetId: string,
  ) => Promise<void>;
}

const useCampaignDetailStore = create<CampaignDetailState>((set, get) => ({
  campaignDetail: null,
  languageObjectiveDashboard: null,

  getCampaignDetailData: async (campaignId: string) => {
    try {
      const response = await apiClient.get<CampaignDetailResponse>(
        `/campaign/main-info/${campaignId}`,
      );
      set({ campaignDetail: response.data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Something went wrong";
      throw new Error(message);
    }
  },

  getLanguageObjectiveDashboard: async (
    campaignId: string,
    languageId: string,
    objectiveId: string,
  ) => {
    try {
      const response = await apiClient.get<LanguageObjectiveDashboardResponse>(
        `/campaign-dashboard/${campaignId}/language/${languageId}/objective/${objectiveId}`,
      );
      set({ languageObjectiveDashboard: response.data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Failed to fetch language-objective dashboard";
      throw new Error(message);
    }
  },

  putS3Image: async (presignedUrl: string, file: File) => {
    try {
      const res = await axios.put(presignedUrl, file, {
        headers: { "Content-Type": file.type },
      });
      if (res.status !== 200)
        throw new Error(`Unexpected status ${res.status}`);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("[S3 Upload Error]", axiosError.response?.status);
      throw new Error("Failed to upload image to S3");
    }
  },

  uploadDashboardImage: async (
    campaignId: string,
    requestBody: UploadDashboardImageRequest,
  ) => {
    try {
      const response = await apiClient.post<
        UploadDashboardImageResponse | InitiateUploadResponse
      >(`/campaign-dashboard/${campaignId}/confirm-header-image`, requestBody);
      if ("upload_url" in response.data.data)
        return response.data as InitiateUploadResponse;
      return response.data as UploadDashboardImageResponse;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Failed to upload dashboard image";
      throw new Error(message);
    }
  },

  uploadAssetsDashboardImage: async (
    campaignId: string,
    requestBody: UploadAssetsDashboardImageRequest,
  ) => {
    try {
      const response = await apiClient.post<
        UploadDashboardImageResponse | InitiateUploadResponse
      >(`/campaign-dashboard/${campaignId}/confirm-header-image`, requestBody);
      if ("upload_url" in response.data.data)
        return response.data as InitiateUploadResponse;
      return response.data as UploadDashboardImageResponse;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Failed to upload assets dashboard image";
      throw new Error(message);
    }
  },

  deleteFlight: async (flightId: string) => {
    try {
      await apiClient.delete(`/flights/${flightId}`);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to delete flight";
      throw new Error(message);
    }
  },

  deleteAsset: async (assetId: string) => {
    try {
      await apiClient.delete(`/assets/${assetId}`);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to delete asset";
      throw new Error(message);
    }
  },

  restoreFlight: async (flightId: string) => {
    try {
      const response = await apiClient.post<RestoreResponse>(
        `/flights/${flightId}/restore`,
      );
      return response.data.data;
    } catch (error) {
      throw toRestoreError(error, "Failed to restore flight");
    }
  },

  restoreAsset: async (assetId: string) => {
    try {
      const response = await apiClient.post<RestoreResponse>(
        `/assets/${assetId}/restore`,
      );
      return response.data.data;
    } catch (error) {
      throw toRestoreError(error, "Failed to restore asset");
    }
  },

  deletedItems: [],

  getDeletedItems: async (campaignId: string) => {
    try {
      const response = await apiClient.get<DeletedItemsResponse>(
        `/campaigns/${campaignId}/deleted-items`,
      );
      set({ deletedItems: response.data.data ?? [] });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to load recently deleted items";
      throw new Error(message);
    }
  },

  uploadAssetPreviewOverride: async (
    campaignId: string,
    assetId: string,
    file: File,
  ) => {
    try {
      // multipart/form-data with a single `image` field. The apiClient request
      // interceptor drops the JSON Content-Type for FormData so the browser can
      // set the multipart boundary automatically.
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiClient.post<AssetPreviewOverrideResponse>(
        `/campaign/${campaignId}/asset/${assetId}/preview-override`,
        formData,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      // Surface field-level 422 validation details when present, otherwise the
      // top-level message (e.g. 404 "Campaign or asset not found.").
      const validationErrors = axiosError.response?.data?.errors;
      const firstValidationMessage =
        validationErrors && typeof validationErrors === "object"
          ? (Object.values(validationErrors)[0] as string[] | undefined)?.[0]
          : undefined;
      const message =
        firstValidationMessage ||
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to upload preview override";
      throw new Error(message);
    }
  },

  removeAssetPreviewOverride: async (campaignId: string, assetId: string) => {
    try {
      await apiClient.delete(
        `/campaign/${campaignId}/asset/${assetId}/preview-override`,
      );
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to remove preview override";
      throw new Error(message);
    }
  },
}));

export default useCampaignDetailStore;
export type {
  AdCategory,
  AdType,
  LanguageObjectiveDashboardData,
  DisplayPayload,
  DisplayPreviewEntry,
  PreviewStatus,
};
